import upperFirst from 'lodash/upperFirst';
import camelCase from 'lodash/camelCase';

import { translate } from '@front/i18n';

import { ProGender } from '../types/commons';

type ParsedTextItem = {
  text: string;
  bold: boolean;
};

const t = translate('base');

const FRENCH_VOWELS = ['a', 'e', 'i', 'o', 'u', 'y', 'h'];

const accentsMap = {
  A: 'Á|À|Ã|Â|Ä',
  a: 'á|à|ã|â|ä',
  E: 'É|È|Ê|Ë',
  e: 'é|è|ê|ë',
  I: 'Í|Ì|Î|Ï',
  i: 'í|ì|î|ï',
  O: 'Ó|Ò|Ô|Õ|Ö',
  o: 'ó|ò|ô|õ|ö',
  U: 'Ú|Ù|Û|Ü',
  u: 'ú|ù|û|ü',
  C: 'Ç',
  c: 'ç',
  N: 'Ñ',
  n: 'ñ',
};

const textParser = (text: string, bold: boolean): ParsedTextItem[] => {
  const sepIndex = text.indexOf('**');
  if (sepIndex === -1) return [{ text, bold }];
  if (sepIndex === 0) return textParser(text.substring(2), !bold);
  return [{ text: text.substring(0, sepIndex), bold }, ...textParser(text.substring(sepIndex + 2), !bold)];
};

export const isStringEmpty = (stringToTest?: string | null) => !stringToTest || stringToTest.trim() === '';

export const parseText = (text: string) => textParser(text, false).filter((item: ParsedTextItem) => !!item.text);

// https://stackoverflow.com/a/44078785
export const guid = () => Date.now().toString(36) + Math.random().toString(36).substring(2);

export const definiteArticleSingular = (noun: string, gender: ProGender) => {
  if (FRENCH_VOWELS.includes(noun.substring(0, 1).toLowerCase())) {
    return t('.definite_article.apostrophe', { noun });
  }
  if (gender === 'woman') return t('.definite_article.woman', { noun });
  return t('.definite_article.man', { noun });
};

export const getSingularPossessiveAdjective = (noun: string, gender: ProGender) => {
  if (FRENCH_VOWELS.includes(noun.substring(0, 1).toLowerCase()) || gender === 'man' || gender === 'dont_care') {
    return t('.possessive_adjective.first_person_singular.man', { noun });
  }

  return t('.possessive_adjective.first_person_singular.woman', { noun });
};

export const toPascalCase = (string: string) => upperFirst(camelCase(string));

export const removeTrailingSlash = (value: string | undefined) => (value ? value.replace(/\/$/, '') : '');

export const removeAccents = (string: string) =>
  [...(Object.keys(accentsMap) as Array<keyof typeof accentsMap>)].reduce(
    (acc: string, key) => acc.replace(new RegExp(accentsMap[key], 'g'), key),
    string,
  );
